// Helpers partagés de la surveillance des sites web (liste, fiche, modale, fiche appareil).

import { api } from './api.jsx';
import { fmtRelativeFromNow } from './format.jsx';

/** Appel qui rend aussi le corps d'erreur (`{ error }`) au lieu de null. */
export async function wmCall(path, method = 'GET', body = undefined) {
  const res = await api.fetch('/platform/web-monitors' + path, {
    method,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  let json = null;
  try {
    const t = await res.text();
    json = t ? JSON.parse(t) : null;
  } catch {
    /* corps non JSON */
  }
  return { ok: res.ok, status: res.status, json };
}

export const STATUS_PENDING = 0;
export const STATUS_UP = 1;
export const STATUS_DOWN = 2;

/** État affiché : pause d'abord (l'état stocké n'a plus de sens), puis panne, confirmation, service. */
export function monitorState(m) {
  if (!m.enabled) return { key: 'paused', label: 'En pause', tone: 'text-muted-2', dot: 'bg-muted-3' };
  if (m.status === STATUS_DOWN)
    return { key: 'down', label: 'En panne', tone: 'text-crit', dot: 'bg-crit', since: m.statusSince };
  if (m.consecutiveFailures > 0)
    return { key: 'confirming', label: 'Échec, confirmation…', tone: 'text-warn', dot: 'bg-warn' };
  if (m.status === STATUS_UP) return { key: 'up', label: 'En service', tone: 'text-ok', dot: 'bg-ok', since: m.statusSince };
  return { key: 'pending', label: 'En attente', tone: 'text-muted-2', dot: 'bg-muted-3' };
}

export function StatusDot({ m, size = 'w-2.5 h-2.5' }) {
  const s = monitorState(m);
  return (
    <span className={`relative inline-flex ${size}`}>
      {s.key === 'down' && <span className={`absolute inset-0 rounded-full ${s.dot} opacity-60 animate-ping`} />}
      <span className={`relative inline-flex rounded-full ${size} ${s.dot}`} />
    </span>
  );
}

/** « 3 h 9 min », « 5 min 24 s », « 0 s ». */
export function fmtDuration(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return s % 60 ? `${m} min ${s % 60} s` : `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d} j ${h % 24} h` : `${d} j`;
}

/** 100 → « 100 % », 99.4943 → « 99,49 % » : deux décimales seulement quand ça compte. */
export function fmtPct(p) {
  if (p == null) return '—';
  if (p >= 100) return '100 %';
  return p.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' %';
}

export const pctTone = (p) => (p == null ? 'text-muted-2' : p >= 99.9 ? 'text-ok' : p >= 99 ? 'text-warn' : 'text-crit');

export function fmtInterval(seconds) {
  if (seconds % 3600 === 0) return `${seconds / 3600} h`;
  if (seconds % 60 === 0) return `${seconds / 60} min`;
  return `${seconds} s`;
}

/** Jours restants avant l'expiration du certificat ; null hors HTTPS. */
export function certDaysLeft(m) {
  if (!m.certExpiresAt) return null;
  return Math.floor((new Date(m.certExpiresAt).getTime() - Date.now()) / 86400000);
}

export function certTone(m) {
  const d = certDaysLeft(m);
  if (d == null) return 'text-muted-2';
  if (d < 0 || d <= 7) return 'text-crit';
  if (d <= Math.max(14, m.certWarningDays || 0)) return 'text-warn';
  return 'text-fg-2';
}

/** Barre des 24 dernières heures : une case par heure, % en service. */
export function UptimeBars({ hours, className = '' }) {
  const now = new Date();
  return (
    <div className={`flex items-end gap-[2px] ${className}`}>
      {(hours || []).map((p, i) => {
        const start = new Date(now);
        start.setMinutes(0, 0, 0);
        start.setHours(start.getHours() - (hours.length - 1 - i));
        const label =
          start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) +
          ' — ' +
          (p == null ? 'pas surveillé' : p >= 100 ? 'en service' : fmtPct(p) + ' en service');
        const cls = p == null ? 'bg-rule-2' : p >= 100 ? 'bg-ok' : p <= 0 ? 'bg-crit' : 'bg-warn';
        return <span key={i} title={label} className={`w-[5px] h-[16px] rounded-[1.5px] ${cls}`} />;
      })}
    </div>
  );
}

/** Texte « En service depuis 7 j 21 h » / « En panne depuis 12 min ». */
export function stateSinceText(m) {
  const s = monitorState(m);
  if ((s.key === 'up' || s.key === 'down') && s.since) return `${s.label} depuis ${fmtRelativeFromNow(s.since)}`;
  return s.label;
}

export const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
